import { Button, Footer, Input, Paper } from 'src/components';
import PasswordInput from 'src/components/PasswordInput';
import { useOnboardingContext } from '../context';
import StepBar from '../components/StepBar';

import styles from '../styles.module.scss';
import { useAuthUser } from 'react-auth-kit';
import { useUserQuery } from 'src/api/queries/userManagementQueries';
import useForm from 'src/hooks/useForm';
import { useEffect } from 'react';
import { useEditUserManagementMutation } from 'src/api/mutations/userManagementMutation';
import { useUpdateOnboardingProgressMutation } from 'src/api/mutations/onboardingMutations';
import { useAlerts } from 'src/common/AlertManager';
import useErrorMessages from 'src/hooks/useErrorsMessage';
import { useParams } from 'react-router-dom';
import UserSchema from 'src/schemas/user';
import PasswordSchema from 'src/schemas/password';

import * as Yup from 'yup';

type OnboardingParams = {
  code: string;
};

const UserOnboardingSchema = UserSchema.concat(PasswordSchema).omit(['oldPassword']);

const Profile = () => {
  const { goToNextStep, goToPreviousStep, userEmail, orgName, profileCompleted, savedPassword, savedProfileForm, markProfileCompleted } = useOnboardingContext();
  const auth = useAuthUser();
  const authenticatedUser = auth();

  const { code } = useParams<OnboardingParams>();

  const { showAlert } = useAlerts();
  const { getErrorMessage } = useErrorMessages();

  const { data: userData, isLoading } = useUserQuery(authenticatedUser?.userId);

  const { form, setForm, handleChange, formErrors, setFieldError, clearFormErrors } = useForm({
    email: '',
    firstName: '',
    lastName: '',
    newPassword: '',
    confirmPassword: '',
  });

  const progressMutation = useUpdateOnboardingProgressMutation();

  const editUserMutation = useEditUserManagementMutation(authenticatedUser?.userId, {
    onSuccess: () => {
      markProfileCompleted(form.newPassword, {
        email: form.email,
        firstName: form.firstName,
        lastName: form.lastName,
        newPassword: form.newPassword,
      });
      progressMutation.mutate({ checklist: { account_setup: true } });
      goToNextStep();
    },
    onError: (error) => {
      showAlert(getErrorMessage(error.message));
    },
  });

  const formChanged = profileCompleted && savedProfileForm
    ? form.email !== savedProfileForm.email
      || form.firstName !== savedProfileForm.firstName
      || form.lastName !== savedProfileForm.lastName
      || form.newPassword !== savedProfileForm.newPassword
    : true;

  const handleGoToNextStep = () => {
    clearFormErrors();
    try {
      UserOnboardingSchema.validateSync(form, { abortEarly: false });

      if (profileCompleted && !formChanged) {
        goToNextStep();
        return;
      }

      editUserMutation.mutateAsync({
        email: form.email,
        first_name: form.firstName,
        last_name: form.lastName,
        old_password: profileCompleted && savedPassword ? savedPassword : code,
        new_password: form.newPassword,
      });
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        error.inner.forEach((err) => {
          if (!err.path) return;
          setFieldError(err.path, err.message);
        });
      }
    }
  };

  // Seed the form from whichever source resolves first: the activation
  // response (context userEmail) and/or GET /users/{id}. The email input
  // renders disabled={!!userEmail}, so its value must populate as soon as
  // userEmail exists — waiting for the user query left the field locked AND
  // blank whenever the two raced (issue #1153 finding 6).
  useEffect(() => {
    setForm((prev: typeof form) => ({
      ...prev,
      email: userEmail || userData?.data?.email || prev.email,
      firstName: userData?.data?.first_name ?? prev.firstName,
      lastName: userData?.data?.last_name ?? prev.lastName,
    }));
  }, [userData, userEmail]);

  return (
    <main className={styles.step}>
      <StepBar
        onGoToNextStep={handleGoToNextStep}
        onGoToPreviousStep={goToPreviousStep}
        loadingNextStep={editUserMutation.isLoading}
        hasUnsavedChanges
      />
      <section className={styles.container}>
        <p className={styles.stepInfo}>Your Profile</p>
        <Paper className={styles.content}>
          <div>
            <Input
              label="First Name"
              variant="secondary"
              value={form.firstName}
              onChange={(value) => handleChange('firstName', value)}
              error={formErrors.firstName}
            />
            <Input
              label="Last Name"
              variant="secondary"
              value={form.lastName}
              onChange={(value) => handleChange('lastName', value)}
              error={formErrors.lastName}
            />
          </div>

          <Input
            label="Email"
            variant="secondary"
            value={form.email}
            onChange={(value) => handleChange('email', value)}
            error={formErrors.email}
            disabled={!!userEmail}
          />

          <PasswordInput
            value={form.newPassword}
            onChange={(value) => handleChange('newPassword', value)}
            error={formErrors.newPassword}
          />

          <Input
            type="password"
            label="Confirm Password"
            variant="secondary"
            value={form.confirmPassword}
            onChange={(value) => handleChange('confirmPassword', value)}
            error={formErrors.confirmPassword}
          />
        </Paper>
      </section>
      <div className={styles.setupBottomCta}>
        <Button size="small" onClick={handleGoToNextStep} loading={editUserMutation.isLoading}>
          Save and continue
        </Button>
      </div>
      <Footer />
    </main>
  );
};

export default Profile;
